import { useEffect, useState } from 'react'
import styled from 'styled-components'
import Link from 'next/link'
import Image from 'next/legacy/image'
import Wrapper from 'common/wrapper'
import { P, Typography } from 'common/typography'
import { SecondaryButton } from 'common/button'
import ContentRenderer from 'content-renderer'
import { useCourse } from 'querys/course'
import useDiff from 'hooks/use-diff'
import { useCampaign } from 'querys/growth'
import { useSubscriptionStatus } from 'querys/subscriptions'

const ProductBannerStyled = styled.section`
  background: var(--neutral100);
  font-family: var(--primaryFont);
  padding: 32px 0;
  margin-top: 80px;
  h2 {
    margin-bottom: 16px;
  }
  p {
    margin-bottom: 24px;
  }
  .product-banner {
    display: flex;
    flex-direction: column;
    align-items: center;
    &-content {
      margin-top: 24px;
    }
  }
  .free-course {
    background: var(--secondary);
    border-radius: 4px;
    padding: 0 4px;
    width: fit-content;
  }
  @media (min-width: 768px) {
    margin-top: 128px;
    .product-banner {
      flex-direction: row;
      align-items: center;
      justify-content: center;
      gap: 16px;
      &-content {
        margin-top: 0px;
      }
      &-title {
        display: flex;
        align-items: center;
        gap: 8px;
      }
    }
  }
`

function Banner({
  img: {
    src, alt, width, height,
  }, title, cta, link, description, is_free, chunks,
}) {
  return (
    <ProductBannerStyled>
      <Wrapper maxWidth="790">
        <div className="product-banner">
          <div className="product-banner-image">
            {src ? (
              <Image width={width} height={height} src={src} alt={alt} />
            ) : null}
          </div>
          <div className="product-banner-content">
            <div className="product-banner-title">
              <Typography as="h2" font="headline5">{title}</Typography>
              {is_free ? (
                <Typography className="free-course" font="smallRegular" color="var(--white)" fontWeight={600}>Curso gratuito</Typography>
              ) : null}
            </div>
            {description ? (
              <P color="var(--textOpaque)">{description}</P>
            ) : null}
            {chunks ? (
              <ContentRenderer chunks={chunks} color="var(--textOpaque)" />
            ) : null}
            <Link href={link} passHref>
              <SecondaryButton id="general-product" fullWidth fitContent>{cta}</SecondaryButton>
            </Link>
          </div>
        </div>
      </Wrapper>
    </ProductBannerStyled>
  )
}

function ProductBanner({ course }) {
  const { data: courseData, isLoading } = useCourse(course)
  const [endDate, setEndDate] = useState(null)
  const { data: campaign, isError } = useCampaign()
  const { diff: diffInTime } = useDiff(endDate)
  const { data: { is_active } } = useSubscriptionStatus({
    product: 'ALL', recurring: 'year',
  })

  useEffect(() => {
    setEndDate(campaign?.coupon?.expires_at)
  }, [campaign?.coupon])

  if (isLoading) return null

  const diff = diffInTime || (campaign?.coupon?.status === 'VALID' && !endDate)

  if (diff && !is_active) {
    const {
      campaign_title, campaign_subtitle, campaign_img, campaign_url,
    } = campaign
    return (
      <Banner
        img={{
          width: '128',
          height: '128',
          src: campaign_img,
          alt: campaign_title,
        }}
        title={campaign_title}
        description={campaign_subtitle}
        link={campaign_url}
        cta="Aprovecha la oferta"
      />
    )
  }

  if (courseData) {
    const {
      slug, title, summary: { blocks }, badge, is_free,
    } = courseData

    return (
      <Banner
        img={{
          width: '128',
          height: '128',
          src: badge?.name && badge?.image ? badge.image : null,
          alt: 'Medalla del curso',
        }}
        title={title}
        link={`/cursos/${slug}`}
        cta="Ver curso"
        is_free={is_free}
        chunks={blocks}
      />
    )
  }

  return (
    <Banner
      img={{
        width: '384',
        height: '292',
        src: '/images/home/spacial-station.png',
        alt: 'Ilustracion de una estacion espacial flotante que toma como base nuestro logo',
      }}
      title="Inicia tu carrera en el mundo de la programación"
      description="Empieza desde 0 y desarrolla todas las habilidades para conseguir un trabajo en tecnología"
      link="/aprender"
      cta="Explorar cursos"
    />
  )
}

export default ProductBanner
